import { useStoreState } from '@discover-data/stores/restaurant/hooks';
import useShouldShowFavouriteFilter from '@discover-data/stores/hooks/favourite/useShouldShow';
import { type RestaurantTagType } from '@discover-data/stores/restaurant/gql/fragments/restaurantTag';
import { type SuburbType } from '@discover-data/gql/fragments/suburb';
import { type PeopleOption } from '@discover-data/stores/hooks/people/options';
import ExitRegularOnlyButton from '@discover-components/restaurant/list/exitRegularTabeOnlyButton';
import MapButton from '@discover-components/map/button';
import RestaurantStatusesFilter from '@discover-components/restaurant/filters/restaurantStatuses/filter';
import TimeRangeFilter from '@discover-components/restaurant/filters/time/range';
import Separator from '@discover-ui/separator';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import deepMemo from '@/components/hocs/deepMemo';
import DiscountOnlySwitch from './discountOnly/switch';
import FavouriteOnlySwitch from './favouriteOnly/switch';
import FiltersAccordion from './accordion';

export type DesktopSideBarProps = {
  defaultMapViewState: ViewState;
  tags: RestaurantTagType[];
  suburbs: SuburbType[];
  peopleOptions?: PeopleOption[] | null;
  minDiners?: PeopleOption | null;
  maxDiners?: PeopleOption | null;
};

function DesktopSideBar({
  defaultMapViewState,
  tags,
  suburbs,
  peopleOptions,
  minDiners,
  maxDiners,
}: DesktopSideBarProps) {
  const discoverEnabled = useStoreState((state) => state.discoverEnabled);
  const regularOnly = useStoreState((state) => state.regularOnly.value);
  const shouldShowFavourite = useShouldShowFavouriteFilter();

  return (
    <TrackProvider section="restaurant filters">
      {/* Keep the sticky sidebar's containing area tall enough while results reload. */}
      <aside className="hidden min-h-screen w-full max-w-[24%] supports-[height:100dvh]:min-h-[100dvh] lg:block">
        <div className="border-border sticky top-[140px] -mr-8 flex h-full max-h-[calc(100vh-140px)] flex-col gap-5 overflow-y-auto pb-6 pl-10 pr-5 supports-[height:100dvh]:max-h-[calc(100dvh-140px)] lg:border-l">
          <MapButton
            className="mb-2"
            defaultViewState={defaultMapViewState}
            tags={tags}
            suburbs={suburbs}
            peopleOptions={peopleOptions}
            minDiners={minDiners}
            maxDiners={maxDiners}
          />
          <Separator />
          {shouldShowFavourite && (
            <>
              <FavouriteOnlySwitch className="my-2" />
              <Separator />
            </>
          )}
          {discoverEnabled && !regularOnly && (
            <>
              <DiscountOnlySwitch className="my-2" />
              <Separator />
            </>
          )}
          {regularOnly && (
            <>
              <ExitRegularOnlyButton />
              <Separator />
            </>
          )}
          <TimeRangeFilter />
          <Separator />
          <FiltersAccordion tags={tags} suburbs={suburbs} />
          <RestaurantStatusesFilter />
        </div>
      </aside>
    </TrackProvider>
  );
}

export default deepMemo(DesktopSideBar);
